<template>
    <div>
        <div class="bg-white border-2 border-gray-100 shadow-sm">
            <div class="relative w-full flex justify-center items-center" ref="container">
                <div
                    ref="editor"
                    class="photo-editor relative block mx-auto"
                >

                    <div v-if="loading">
                        <div class="absolute top-0 right-0 w-full h-full bg-white opacity-90"></div>
                        <div class="absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <svg class="h-12 w-12 animate-spin mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    </div>

                    <img ref="photo" :src="photo.original_path" crossorigin />
                </div>
            </div>
        </div>

        <!-- Actions and info -->
        <div class="flex-shrink-0 mt-6 px-4 sm:px-6">

            <div class="flex justify-center items-center gap-2 flex-col sm:flex-row">
                <div class="isolate inline-flex rounded-md shadow-sm" ref="toolbar">
                    <button
                        v-for="(action, index) in actions"
                        :key="index"
                        type="button"
                        class="relative inline-flex items-center border border-gray-300 bg-white px-2 xxs:px-3 xs:px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-0 focus:outline-none"
                        :class="{
                            'rounded-l-md': index === 0,
                            'rounded-r-md': index === (actions.length - 1),
                            '-ml-px': index !== 0,
                        }"
                        @click="handleAction(action.action, action.value)"
                    >
                        <component
                            class="w-5 h-5 sm:w-6 sm:h-6"
                            :is="action.icon"
                        />
                    </button>
                </div>

                <div class="isolate inline-flex rounded-md shadow-sm" ref="toolbar">
                    <button
                        v-for="(filter, index) in filters"
                        :key="index"
                        type="button"
                        class="relative inline-flex items-center border border-gray-300 bg-white px-2 xxs:px-3 xs:px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-0 focus:outline-none"
                        :class="{
                            'rounded-l-md': index === 0,
                            'rounded-r-md': index === (filters.length - 1),
                            '-ml-px': index !== 0,
                        }"
                        @click="addFilter(filter.key)"
                    >
                        {{ filter.label }}
                    </button>
                </div>
            </div>

            <p class="w-full text-center text-gray-500 text-sm my-4">{{ __('home::products.image_editor_help_text') }}</p>

        </div>

        <div class="mt-2 space-y-1">

            <div
                v-if="photo.warnings?.includes('low_resolution')"
                class="bg-red-100 text-red-700 px-3 py-2 text-sm rounded-md"
            >
                {{ __('home::orders.warning_low_resolution') }}
            </div>

            <div
                v-if="isSmallerThanCanvas"
                class="bg-yellow-100 text-yellow-700 px-3 py-2 text-sm rounded-md"
            >
                {{ __('home::orders.warning_smaller_than_canvas') }}
            </div>

        </div>

    </div>
</template>

<script>
import axios from 'axios'
import Cropper from 'cropperjs'

import {
    ZoomInIcon,
    ZoomOutIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
} from '@heroicons/vue/outline'

export default {
    components: {
        ZoomInIcon,
        ZoomOutIcon,
        ChevronUpIcon,
        ChevronDownIcon,
        ChevronRightIcon,
        ChevronLeftIcon,
    },
    props: {
        photo: Object,
        order: Object,
    },
    data() {
        return {
            loading: true,
            cropper: null,
            filter: null,
            isSmallerThanCanvas: false,
            actions: [
                { icon: ZoomInIcon, action: 'zoom', value: 0.05 },
                { icon: ZoomOutIcon, action: 'zoom', value: -0.05 },
                { icon: ChevronUpIcon, action: 'move', value: [0, -5] },
                { icon: ChevronDownIcon, action: 'move', value: [0, 5] },
                { icon: ChevronLeftIcon, action: 'move', value: [-5, 0] },
                { icon: ChevronRightIcon, action: 'move', value: [5, 0] },
            ],
            filters: [
                { key: '', label: this.__('home::orders.filter_none') },
                { key: 'greyscale', label: this.__('home::orders.filter_greyscale') },
            ],
        }
    },
    mounted() {

        var t = this;

        t.setDimensions();
        window.addEventListener("resize", t.setDimensions);

        t.cropper = new Cropper(t.$refs.photo, {
            aspectRatio: 1 / t.photo.product.ratio,
            viewMode: 0, // 1
            dragMode: 'move',
            autoCropArea: 1,
            restore: false,
            modal: false,
            guides: false,
            highlight: true,
            center: false,
            cropBoxMovable: false,
            cropBoxResizable: false,
            toggleDragModeOnDblclick: false,
            checkCrossOrigin: false,
            ready() {

                t.cropper.dragBox.style.backgroundImage = "url('" + t.photo.product.primary_image.public_path + "')";

                t.setDimensions();

                t.loading = false;

                if (t.photo.modifications) {

                    if ('cropper' in t.photo.modifications) {
                        this
                            .cropper
                            //.setCropBoxData(t.photo.modifications.cropper.crop_box)
                            .setCanvasData(t.photo.modifications.cropper.canvas);
                    }

                    if ('filter' in t.photo.modifications && t.photo.modifications.filter) {
                        t.addFilter(t.photo.modifications.filter);
                    }

                }
                else {
                    var aspectRatio = 1 / t.photo.product.ratio;
                    var containerData = this.cropper.getContainerData();
                    var cropBoxData = this.cropper.getCropBoxData();
                    var canvasData = this.cropper.getCanvasData();

                    var widthRatio = cropBoxData.width / canvasData.naturalWidth;
                    var heightRatio = cropBoxData.height / canvasData.naturalHeight;
                    var ratio = widthRatio > heightRatio ? widthRatio : heightRatio;

                    var position = {
                        x: cropBoxData.left + (cropBoxData.width / 2),
                        y: cropBoxData.top + (cropBoxData.height / 2),
                    };

                    this.cropper.zoomTo(ratio, position);

                    var canvasData = this.cropper.getCanvasData();
                    this.cropper.moveTo(position.x - (canvasData.width / 2), position.y - (canvasData.height / 2));
                }
            },
            crop(event) {

                var cropBoxData = this.cropper.cropBoxData;
                var canvasData = this.cropper.canvasData;

                if (
                    event.detail.x < 0
                    || event.detail.y < 0
                    || (canvasData.width + canvasData.left - cropBoxData.left) < cropBoxData.width
                    || (canvasData.height + canvasData.top - cropBoxData.top) < cropBoxData.height
                ) {
                    t.isSmallerThanCanvas = true;
                }
                else {
                    t.isSmallerThanCanvas = false;
                }
            },
        });
    },
    methods: {
        setDimensions() {
            let containerWidth = this.$refs.container?.offsetWidth || 0;
            let maxSize = window.innerHeight > 500 ? (window.innerHeight - 400) : containerWidth;
            let size = Math.min(maxSize, containerWidth);

            if (this.$refs.editor) {
                this.$refs.editor.style.width = size + 'px';
                this.$refs.editor.style.height = size + 'px';
            }

            if (this.cropper) {

                var padding = {
                    top: size * this.photo.product.padding.top / 100,
                    right: size * this.photo.product.padding.right / 100,
                    bottom: size * this.photo.product.padding.bottom / 100,
                    left: size * this.photo.product.padding.left / 100,
                };

                var cropBoxData = {
                    top: padding.top,
                    left: padding.left,
                    width: size - padding.left - padding.right,
                    height: size - padding.top - padding.bottom,
                };

                /*
                this.cropper.cropBoxData.minWidth = 0;
                this.cropper.cropBoxData.maxWidth = 9999;
                this.cropper.cropBoxData.minHeight = 0;
                this.cropper.cropBoxData.maxHeight = 9999;
                this.cropper.cropBoxData.minLeft = 0;
                this.cropper.cropBoxData.maxLeft = 9999;
                this.cropper.cropBoxData.minTop = 0;
                this.cropper.cropBoxData.maxTop = 9999;
                */

                this.cropper.setCropBoxData(cropBoxData);
            }
        },
        handleAction(action, value) {
            if (action === 'zoom') {
                this.cropper.zoom(value);
            }
            else if (action === 'move') {
                this.cropper.move(value[0], value[1]);
            }
        },
        addFilter(filter) {

            this.filter = filter;

            if (filter == '') {
                this.cropper.replace(this.photo.original_path, true)
            }
            else {
                this.loading = true;

                let request = {
                    filter: filter,
                };

                axios
                    .post(this.$route('home::orders.photos.add-filter', { uuid: this.order.uuid, photo: this.photo.id }), request)
                    .then((res) => {
                        this.loading = false;
                        this.cropper.replace(res.data, true);
                    });
            }
        },
        save() {
            this.loading = true;

            let request = {
                filter: this.filter,
                cropper: {
                    data: this.cropper.getData(true),
                    canvas: this.cropper.getCanvasData(),
                    crop_box: this.cropper.getCropBoxData(),
                },
            };

            axios
                .post(this.$route('home::orders.photos.save', { uuid: this.order.uuid, photo: this.photo.id }), request)
                .then((res) => {
                    this.loading = false;
                    var photo = res.data;
                    this.$emit('saved', photo);
                });
        },
    },
};
</script>
